import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlockRenderer, { ContentBlock } from '../components/BlockRenderer';
import AuthRequiredLink from '../components/AuthRequiredLink';
import { usePageInit } from '../hooks/usePageInit';
import './NoticeDetailPage.css';

type LpNotice = {
  id: string;
  title?: string;
  content?: string;
  blocks?: ContentBlock[];
  publishedAt?: any;
};

function toMillis(v: any): number | null {
  try {
    if (!v) return null;
    if (typeof v?.toMillis === 'function') return v.toMillis();
    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    const d = new Date(v as any);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
}

function renderLegacyContent(content?: string) {
  if (!content) return null;
  const text = String(content);
  // ざっくりHTMLっぽければHTMLとして表示（旧データ互換）
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(text);
  if (looksLikeHtml) {
    return <div className="notice-legacy-html" dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return (
    <div className="notice-legacy-text">
      {text.split('\n').map((line, idx) => (
        <p key={idx}>{line}</p>
      ))}
    </div>
  );
}

export default function NoticeDetailPage() {
  usePageInit();
  const { noticeId } = useParams<{ noticeId: string }>();
  const [notice, setNotice] = useState<LpNotice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      if (!noticeId) {
        setNotice(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'notices', noticeId));
        if (!snap.exists()) {
          setNotice(null);
          return;
        }
        const data = snap.data() as any;
        setNotice({ id: snap.id, ...data });
      } catch (e) {
        console.error('Error fetching notice:', e);
        setNotice(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [noticeId]);

  const publishedLabel = useMemo(() => {
    const ms = toMillis(notice?.publishedAt);
    if (!ms) return null;
    try {
      return new Date(ms).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return null;
    }
  }, [notice?.publishedAt]);

  const body = useMemo(() => {
    if (!notice) return null;
    if (Array.isArray(notice.blocks) && notice.blocks.length > 0) {
      return <BlockRenderer blocks={notice.blocks} />;
    }
    return renderLegacyContent(notice.content);
  }, [notice]);

  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var" id="main-wrap">
        <div className="main-wrap">
          <Header />
          <div className="notice-detail-page">
            <div className="page-wrap">
              <div className="hidden-md-up">
                <div className="logo logo-header">
                  <AuthRequiredLink to="/">
                    <img src="/assets/images/mobile-logo.png" alt="logo" />
                  </AuthRequiredLink>
                </div>
              </div>
              <div className="container inner-wrap max-md">
                <AuthRequiredLink className="waves-effect btn-icon backtohome fragment-fadeUp" to="/">
                  <span>
                    <i className="ion-ios-home-outline"></i>
                    <i className="ion-ios-arrow-thin-left"></i>
                  </span>
                </AuthRequiredLink>

                <div className="card notice-box fragment-fadeUp">
                  <div className="notice-content">
                    <h1 className="notice-title">{notice?.title || 'お知らせ'}</h1>
                    {publishedLabel && <p className="notice-meta">{publishedLabel}</p>}

                    {loading ? (
                      <div className="loading-container">
                        <p>読み込み中...</p>
                      </div>
                    ) : notice ? (
                      <div className="notice-body">{body || <p>コンテンツがまだ設定されていません。</p>}</div>
                    ) : (
                      <div className="notice-body">
                        <p>お知らせが見つかりませんでした。</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer withDeco={true} />
        </div>
      </div>
    </div>
  );
}


